<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatCurrency } from '@/shared/lib/format-currency';
import IconInfoFlat from '@/shared/assets/icons/info-flat.vue';
import UiBottomSheet from '@/shared/ui/bottom-sheet';

interface Props {
  modelValue: boolean;
  paymentAmount: number;
  availableAmount: number;
  currency: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void;
  (event: 'edit'): void;
  (event: 'continue'): void;
}>();

const { t } = useI18n();
const paymentAmount = computed(() => `${formatCurrency(props.paymentAmount)} ${props.currency}`);
const availableAmount = computed(() => `${formatCurrency(props.availableAmount)} ${props.currency}`);
</script>

<template>
  <ui-bottom-sheet
    :model-value="props.modelValue"
    class="insufficient-funds-bottom-sheet__container"
    :resizable="false"
    :show-handle="true"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <section class="insufficient-funds-bottom-sheet">
      <div class="insufficient-funds-bottom-sheet__icon-wrap" aria-hidden="true">
        <ui-icon name="icon-info" />
      </div>

      <h2 class="insufficient-funds-bottom-sheet__title">
        {{ t('FORM_PAYMENT.INSUFFICIENT_FUNDS_DIALOG.SINGLE_PAYMENT_TITLE') }}
      </h2>

      <div class="insufficient-funds-bottom-sheet__amounts">
        <div class="insufficient-funds-bottom-sheet__amount">
          <span>{{ t('FORM_PAYMENT.INSUFFICIENT_FUNDS_DIALOG.PAYMENT_AMOUNT') }}</span>
          <strong>{{ paymentAmount }}</strong>
        </div>
        <div class="insufficient-funds-bottom-sheet__amount">
          <span>{{ t('FORM_PAYMENT.INSUFFICIENT_FUNDS_DIALOG.AVAILABLE_BALANCE') }}</span>
          <strong>{{ availableAmount }}</strong>
        </div>
      </div>

      <div class="insufficient-funds-bottom-sheet__notice">
        <icon-info-flat class="insufficient-funds-bottom-sheet__notice-icon" aria-hidden="true" />
        <p>{{ t('FORM_PAYMENT.INSUFFICIENT_FUNDS_DIALOG.DESCRIPTION') }}</p>
      </div>

      <div class="insufficient-funds-bottom-sheet__actions">
        <ui-button
          class="insufficient-funds-bottom-sheet__action insufficient-funds-bottom-sheet__action--primary"
          :label="t('FORM_PAYMENT.INSUFFICIENT_FUNDS_DIALOG.EDIT')"
          @click="emit('edit')"
        />
        <ui-button
          class="insufficient-funds-bottom-sheet__action"
          outline
          :label="t('FORM_PAYMENT.INSUFFICIENT_FUNDS_DIALOG.CONTINUE')"
          @click="emit('continue')"
        />
      </div>
    </section>
  </ui-bottom-sheet>
</template>

<style scoped lang="scss">
.insufficient-funds-bottom-sheet {
  display: flex;
  flex-direction: column;
  text-align: center;

  &__container {
    width: 100%;
    max-width: none;
    margin: 0 auto 1rem;
    border-radius: 1.75rem;
    overflow: hidden;
    background: $white;
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 5rem;
    height: 5rem;
    margin: 1rem auto;
    border-radius: 1.25rem;
    background: #f7f6f9;
  }

  &__title {
    padding: 0 1rem;
    margin: 0;
    color: #1c1c1e;
    font-size: 1.25rem;
    line-height: 1.5rem;
    font-weight: 600;
    letter-spacing: 0.02375rem;
  }

  &__amounts {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    margin: 1rem 0 0;
    text-align: left;
  }

  &__amount {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.125rem;
    width: 100%;
    height: 3.5rem;

    & + & {
      border-top: 0.0625rem solid #e9e8eb;
    }

    span {
      color: #6c6c70;
      font-size: 0.875rem;
      line-height: 1.125rem;
    }

    strong {
      color: #1c1c1e;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.25rem;
    }
  }

  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    width: 100%;
    margin: 1.5rem auto 0;
    padding: 0.5rem 0;
    text-align: left;

    p {
      margin: 0;
      color: #1c1c1e;
      font-size: 0.875rem;
      line-height: 1.125rem;
      letter-spacing: -0.004875rem;
    }
  }

  &__notice-icon {
    flex: 0 0 auto;
    width: 1.25rem;
    height: 1.25rem;
    color: #647edc;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
    margin: 1.5rem auto 0;
  }

  &__action {
    width: 100%;
    height: 3rem;
    max-height: none;
    border-radius: 1rem;
    font-size: 1.125rem;
    font-weight: 500;
    line-height: 1.5rem;
    letter-spacing: -0.0255rem;

    &--primary {
      --ui-button-color: #080809;
      --ui-button-text-color: #eaeaea;
    }

    &:not(&--primary) {
      --ui-button-color: #080809;
      --ui-button-plain-color: #1c1c1e;
      --ui-button-text-color: #1c1c1e;
    }
  }
}

:deep(.insufficient-funds-bottom-sheet__container .draggable-area) {
  position: relative;
  right: auto;
  left: auto;
  z-index: 1;
  min-height: 1.25rem;
  height: 1.25rem;
  padding: 0;
  background: $white;
}

:deep(.insufficient-funds-bottom-sheet__container .draggable-thumb) {
  width: 2.25rem;
  height: 0.3125rem;
  background: #d8d8dc;
}

:deep(.insufficient-funds-bottom-sheet__container .content) {
  padding: 0 0 calc(0.5rem + env(safe-area-inset-bottom));
  margin-top: 0;
}

:deep(.insufficient-funds-bottom-sheet__container .ui-button__content) {
  width: 100%;
}

:deep(.insufficient-funds-bottom-sheet__container .ui-button__label) {
  width: 100%;
  text-align: center;
}

:deep(.q-dialog__backdrop) {
  background: rgb(28 28 30 / 40%);
  backdrop-filter: blur(2.5rem);
}
</style>
